import React, { useState, useEffect } from 'react';
import { useTranslate } from 'ra-core';
import { Labeled, useQuery } from 'react-admin';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import IconFileCopy from '@material-ui/icons/FileCopy';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import NativeSelect from '@material-ui/core/NativeSelect';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { makeStyles } from '@material-ui/core/styles';
import get from 'lodash/get';
import { useForm } from 'react-final-form';
import { useLayer7Notify } from '../useLayer7Notify';
import { useCopyToClipboard } from '../ui';
export const ApplicationKeySecret = props => {
    const { id: appUuid, record, isEditDisabled, labelClasses } = props;
    const [open, setOpen] = useState(false);
    const classes = useStyles(props);
    const copyToClipboard = useCopyToClipboard({
        successMessage: 'resources.applications.notifications.copy_success',
        errorMessage: 'resources.applications.notifications.copy_error',
    });
    const translate = useTranslate();
    const notify = useLayer7Notify();
    const form = useForm();
    const [keySecret, setKeySecret] = useState(record.keySecret);
    const [isPlainTextSelected, setIsPlainTextSelected] = useState(true);
    const [isHashedSecretSetting, setIsHashedSecretSetting] = useState(false);
    const [allowSelectHashing, setAllowSelectHashing] = useState(false);
    const [oneTimePassword, setOneTimePassword] = useState(false);
    const handleClick = event => {
        const value = event.target.value;
        if (value === 0) {
            return false;
        }
        if ((!value && isHashedSecretSetting) || value > 1) {
            setIsPlainTextSelected(false);
        }
        else {
            setIsPlainTextSelected(true);
        }
        setOpen(true);
    };
    const handleClose = () => {
        setOneTimePassword(false);
        setOpen(false);
    };
    const handleSecretClearAndClose = () => {
        setKeySecret('***********');
        setOneTimePassword(false);
        setOpen(false);
    };
    const { data, error, loading: isGetSecretHashMetadataLoading } = useQuery({
        type: 'getSecretHashMetadata',
        resource: 'applications',
        payload: {},
    });
    useEffect(() => {
        if (data && data.value) {
            const isPlainTextAllowed = get(JSON.parse(data.value), 'plaintextAllowed');
            setIsHashedSecretSetting(true);
            setAllowSelectHashing(isPlainTextAllowed);
        }
        else {
            setIsHashedSecretSetting(false);
            setIsPlainTextSelected(true);
            setAllowSelectHashing(false);
        }
    }, [data, error]);
    /*
    To generate random uuid
    */
    const uuidv4 = () => {
        return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c => (c ^
            (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16));
    };
    const generateKeySecret = () => {
        const key = uuidv4().replace(/-/g, '');
        notify('resources.applications.notifications.secret_generated_heading');
        if (isHashedSecretSetting && !isPlainTextSelected) {
            setOneTimePassword(true);
            setKeySecret(key);
            form.change('ShouldHash', true);
        }
        else {
            setKeySecret(key);
            setOpen(false);
            form.change('ShouldHash', false);
        }
        form.change('keySecret', key);
    };
    if (isGetSecretHashMetadataLoading) {
        return null;
    }
    return (React.createElement(Grid, null,
        React.createElement(Grid, { item: true },
            React.createElement(Labeled
            // on Labeled, this will translate in a correct `for` attribute on the label
            , { 
                // on Labeled, this will translate in a correct `for` attribute on the label
                id: "sharedSecretClientSecret", label: "resources.applications.fields.sharedSecretClientSecret", classes: labelClasses, className: classes.field },
                React.createElement(Typography, { variant: "body2", className: classes.fieldContent },
                    React.createElement("span", { id: "sharedSecretClientSecret" }, keySecret),
                    keySecret && !keySecret.includes('****') && (React.createElement(IconButton, { className: classes.buttonCopy, color: "primary", title: translate('resources.applications.notifications.copy_to_clipboard'), value: keySecret, onClick: copyToClipboard },
                        React.createElement(IconFileCopy, { className: classes.iconCopy })))))),
        !isEditDisabled && (React.createElement(Grid, { item: true, xs: 12 },
            !allowSelectHashing && (React.createElement(Button, { className: classes.buttonGenerate, color: "primary", variant: "outlined", onClick: handleClick, "aria-label": translate('resources.applications.actions.generateSecret'), value: 0 }, translate('resources.applications.actions.generateSecret'))),
            isHashedSecretSetting && allowSelectHashing && (React.createElement(NativeSelect, { className: classes.selectField, name: translate('resources.applications.actions.generateSecret'), onChange: handleClick, label: translate('resources.applications.actions.generateSecret'), inputProps: {
                    'aria-label': translate('resources.applications.actions.generateSecret'),
                }, value: 0, variant: "outlined", color: "primary" },
                React.createElement("option", { value: 0 }, translate('resources.applications.actions.generateSecret')),
                React.createElement("option", { value: 1 }, translate('resources.applications.actions.plainTextSecret')),
                React.createElement("option", { value: 2 }, translate('resources.applications.actions.hashedSecret')))),
            React.createElement(Dialog, { disableBackdropClick: oneTimePassword, disableEscapeKeyDown: oneTimePassword, open: open, onClose: handleClose, "aria-labelledby": "form-dialog-title" }, oneTimePassword ? (React.createElement(OneTimePasswordDialog, { handleClose: handleClose, keySecret: keySecret })) : (React.createElement(ShowGenerateDialog, { handleClose: handleClose, generate: generateKeySecret })))))));
};
const useStyles = makeStyles(theme => ({
    field: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        marginBottom: theme.spacing(2),
        minWidth: '100px',
    },
    fieldContent: {},
    buttonCopy: {},
    iconCopy: {
        fontSize: '1rem',
        color: theme.palette.secondary.main,
    },
    buttonGenerate: {},
    selectField: {
        border: `1px solid ${theme.palette.primary.main}`,
        color: theme.palette.primary.main,
        borderRadius: 4,
        '&.MuiNativeSelect-select:focus, .MuiNativeSelect-select': {
            borderRadius: 'unset',
            backgroundColor: 'transparent',
        },
        '&.MuiInput-underline:after, .MuiInput-underline:before': {
            borderBottom: 0,
        },
        '&.MuiInput-underline:before': {
            borderBottom: 0,
        },
        '&.MuiInput-underline:hover:not(.Mui-disabled):before': {
            borderBottom: 0,
        },
        padding: theme.spacing(0.5),
        margin: theme.spacing(1),
    },
}), {
    name: 'Layer7ApplicationKeySecret',
});
const useOneTimePasswordDialogStyles = makeStyles(theme => ({
    mainContent: {
        display: 'flex',
        flexDirection: 'row',
    },
    leftSection: {
        flex: '1',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: theme.spacing(0),
        padding: theme.spacing(1),
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.common.white,
    },
    leftIcon: {
        fontSize: '5rem',
        color: theme.palette.common.white,
    },
    rightSection: {
        flex: '5',
        flexDirection: 'row',
    },
    subHeading: {
        textTransform: 'uppercase',
        fontWeight: theme.typography.fontWeightBold,
    },
    copyHashSection: {
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(1),
        textAlign: 'center',
    },
}), {
    name: 'Layer7ApplicationOneTimePasswordDialog',
});
const OneTimePasswordDialog = ({ handleClose, keySecret }) => {
    const classes = useOneTimePasswordDialogStyles();
    const copyToClipboard = useCopyToClipboard({
        successMessage: 'resources.applications.notifications.copy_success',
        errorMessage: 'resources.applications.notifications.copy_error',
    });
    const translate = useTranslate();
    return (React.createElement("div", { className: classes.mainContent },
        React.createElement("div", { className: classes.leftSection },
            React.createElement(ReportProblemOutlinedIcon, { className: classes.leftIcon })),
        React.createElement("div", { className: classes.rightSection },
            React.createElement(DialogTitle, { id: "form-dialog-title" }, translate('resources.applications.notifications.secret_generated_heading')),
            React.createElement(DialogContent, { dividers: true },
                React.createElement(Typography, { variant: "subtitle1", component: "h2", className: classes.subHeading, gutterBottom: true }, translate('resources.applications.notifications.copy_secret_now')),
                React.createElement(Typography, { variant: "body1", gutterBottom: true }, translate('resources.applications.notifications.secret_generated_message')),
                React.createElement("div", { className: classes.copyHashSection },
                    React.createElement(Typography, { variant: "subtitle2", gutterBottom: true }, translate('resources.applications.fields.sharedSecretClientSecret')),
                    React.createElement(Typography, { variant: "body1", gutterBottom: true }, keySecret),
                    React.createElement(Button, { onClick: copyToClipboard, value: keySecret, align: "center", variant: "contained", color: "primary" }, translate('resources.applications.notifications.copy_to_clipboard')))),
            React.createElement(DialogActions, null,
                React.createElement(Button, { onClick: handleClose, color: "secondary" }, translate('ra.action.close'))))));
};
const ShowGenerateDialog = ({ handleClose, generate }) => {
    const translate = useTranslate();
    return (React.createElement(React.Fragment, null,
        React.createElement(DialogTitle, { id: "form-dialog-title" }, translate('resources.applications.actions.generateSecret')),
        React.createElement(DialogContent, { dividers: true },
            React.createElement(Typography, { variant: "body1" }, `${translate('resources.applications.notifications.generate_secret_warning_1')}
                    ${translate('resources.applications.notifications.generate_secret_warning_2')}`)),
        React.createElement(DialogActions, null,
            React.createElement(Button, { onClick: handleClose, variant: "outlined", color: "secondary" }, translate('ra.action.cancel')),
            React.createElement(Button, { onClick: generate, variant: "contained", color: "primary" }, translate('resources.applications.actions.generateSecret')))));
};
//# sourceMappingURL=ApplicationKeySecret.js.map